/**
 * First-party product activity logging (separate from GTM/consent analytics).
 * Fire-and-forget POST to /api/activity/log — never blocks or breaks UX.
 */

import apiClient from '../../api/config/ApiClient';

export const ACTIVITY_ACTIONS = {
  TAB_ARBITRAGE: 'TAB_ARBITRAGE',
  TAB_BEST_ODDS: 'TAB_BEST_ODDS',
  TAB_NOTIFICATIONS: 'TAB_NOTIFICATIONS',
  CALC_SUREBET_OPEN: 'CALC_SUREBET_OPEN',
  ANOMALY_DETAIL_OPEN: 'ANOMALY_DETAIL_OPEN'
};

const THROTTLE_MS = 5000;
const lastSentAtByAction = new Map();

/**
 * Log a product activity event. Swallows all errors; no retries.
 * Same action is throttled to at most once per 5 seconds (client-side).
 * @param {string} action - One of ACTIVITY_ACTIONS values
 * @returns {Promise<void>}
 */
export const logActivity = async (action) => {
  if (!action || typeof action !== 'string') {
    return;
  }

  const now = Date.now();
  const lastSent = lastSentAtByAction.get(action) || 0;
  if (now - lastSent < THROTTLE_MS) {
    return;
  }
  lastSentAtByAction.set(action, now);

  try {
    // _retry: true disables ApiClient automatic retry amplification on failure
    await apiClient.post(
      '/api/activity/log',
      { action },
      {
        _retry: true,
        timeout: 5000
      }
    );
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[activityLog] skipped/failed:', action, error?.message || error);
    }
  }
};

export const activityLogService = {
  logActivity,
  ACTIVITY_ACTIONS
};

export default activityLogService;
